import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Empty, notification, Tooltip } from 'antd';
import {
  Button,
  PrioSpinner,
  VirtualList,
  VirtualListRef,
} from '@prio365/prio365-react-library';

import { NavigateFunction, useNavigate } from 'react-router-dom';

import { makePrioStyles } from '../../../theme/utils';
import {
  Contact,
  ExternalContact,
  InternalContact,
} from '../../../models/Contact';
import { Company } from '../../../models/Company';
import {
  ContactId,
  CompanyId,
  PhoneLinePreference,
} from '../../../models/Types';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '../../../components/CopyableTextTile';
import { apiCreateMessageDraft, apiUpdateMessageDraft } from '../../mail/api';
import { getCallingHref } from '../utils';
import useContactsContext from '../hooks/useContactsProvider';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';
import { useSelector } from 'react-redux';
import { getPhoneLinePreference } from '../../../apps/main/rootReducer';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import { debounceFunction } from '../../../util';
import { SortingProps } from '@prio365/prio365-react-library/lib/VirtualList/hooks/useSortBy';

const listItemHeight = 58;

const debouncedActiveItemChanged = debounceFunction(
  (
    item: ContactListItem,
    navigate: NavigateFunction,
    pathPrefix?: string,
    onContactClick?: (contactId: ContactId) => void,
    onCompanyClick?: (companyId: CompanyId) => void
  ) => {
    if (item.type === 'contact') {
      const contact = item.value as ExternalContact & InternalContact;
      if (onContactClick) {
        onContactClick(contact.contactId);
      } else {
        navigate(`${pathPrefix ?? ''}contact/${contact.contactId}/details`);
      }
    } else {
      if (item.type === 'company') {
        const company = item.value as Company;
        if (onCompanyClick) {
          onCompanyClick(company.companyId);
        } else {
          navigate(`${pathPrefix ?? ''}company/${company.companyId}/details`);
        }
      }
    }
  },
  250
);

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
  },
  loadingOverlay: {
    background: '#FFFFFFBF',
    position: 'absolute',
    top: 0,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  showMore: {
    width: '100%',
    borderTop: theme.old.borders.content,
    '&:hover': {
      borderTop: theme.old.borders.content,
    },
    height: 64,
  },
  noData: {
    display: 'flex',
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  listItemContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  listItemActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  listItemMainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',

    '& > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  archivedContact: {
    fontStyle: 'italic',
    color: 'var(--prioCl-color-application-typography-muted)',
  },
  tryAnyway: {
    marginTop: theme.spacing.small,
    color: theme.colors.base.primary.default,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: theme.font.fontSize.regular,
    display: 'flex',
    justifyContent: 'center',
  },
  noItemsScreen: {
    width: '100%',
    height: '100%',
    position: 'relative',
    padding: theme.spacing.regular,
    display: 'flex',
    alignItems: 'center',
  },
  noItemsIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '200px',
    height: '200px',
    position: 'absolute',
    left: 'calc(50% - 100px)',
    top: 'calc(50% - 100px)',
  },
  noItemsText: {
    textAlign: 'center',
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.small,
  },
  searchAnywayContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  searchAnywayText: {
    textAlign: 'center',
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
  },
}));

export type ContactListRefProps = VirtualListRef<ContactListItem> & {
  getSelectedItems: () => ContactListItem[];
};

export declare type ContactListType = 'contact' | 'company';

export interface ContactListItem {
  type: ContactListType;
  value: Contact | Company;
}

interface ContactListProps {
  className?: string;
  listItemClassName?: string;
  items: ContactListItem[];
  propSearchItemCount: number;
  selectable?: boolean;
  onSelect?: (items: ContactListItem[]) => void;
  onContactClick?: (contactId: ContactId) => void;
  onCompanyClick?: (companyId: CompanyId) => void;
  pathPrefix?: string;
  onlineLoading?: boolean;
  searchAnyway?: boolean;
  setSearchAnyway?: (value: boolean) => void;
}

export const ContactList = React.memo(
  forwardRef(
    (props: ContactListProps, ref: React.ForwardedRef<ContactListRefProps>) => {
      //#region ------------------------------ Defaults
      const classes = useStyles();
      const { t } = useTranslation();

      const navigate = useNavigate();
      const {
        className,
        listItemClassName,
        items,
        propSearchItemCount,
        selectable,
        onSelect,
        onContactClick,
        onCompanyClick,
        pathPrefix,
        onlineLoading,
        searchAnyway = false,
        setSearchAnyway = () => {},
      } = props;
      //#endregion

      //#region ------------------------------ States / Attributes / Selectors
      const phoneLinePreference = useSelector(getPhoneLinePreference);

      const internalRef = useRef<VirtualListRef<ContactListItem>>(null);

      const selectedItemsRef = useRef<ContactListItem[]>([]);

      const { getCompanyById } = useCompaniesContext();
      const { getContactById } = useContactsContext();
      //#endregion

      //#region ------------------------------ Methods / Handlers
      const handleSelect = useCallback(
        (selectedItems: ContactListItem[]) => {
          selectedItemsRef.current = selectedItems;
          if (onSelect) {
            onSelect(selectedItems);
          }
        },
        [onSelect]
      );

      const handleActiveItemChanged = useCallback(
        (item: ContactListItem) => {
          debouncedActiveItemChanged(
            item,
            navigate,
            pathPrefix,
            onContactClick,
            onCompanyClick
          );
        },
        [navigate, pathPrefix, onContactClick, onCompanyClick]
      );

      const handleOnRow: (
        item: ContactListItem,
        index: number,
        selectedItems: ContactListItem[],
        activeItem: ContactListItem | undefined,
        items: ContactListItem[]
      ) => VirtualListItemOnRowProps<ContactListItem> = useCallback(
        (item) => ({
          onClick: () => {
            handleActiveItemChanged(item);
          },
        }),
        [handleActiveItemChanged]
      );

      const handleOnActiveChange = useCallback(
        (index: number) => {
          const item = items[index];
          if (item) {
            handleActiveItemChanged(item);
          }
        },
        [items, handleActiveItemChanged]
      );
      //#endregion

      //#region ------------------------------ Refs
      useImperativeHandle(
        ref,
        () => ({
          getSelectedItems: () => {
            return selectedItemsRef.current;
          },
          recomputeRowHeights: () => {
            internalRef.current?.recomputeRowHeights();
          },
          forceUpdate: () => {
            internalRef.current?.forceUpdate();
          },
          scrollToRow: (index: number) => {
            internalRef.current?.scrollToRow(index);
          },
          setSelectedIndexes: (indexes: number[]) => {
            internalRef.current?.setSelectedIndexes(indexes);
          },
          setSelectAll: (selectAll: boolean) => {
            internalRef.current?.setSelectAll(selectAll);
          },
          getSelectedIndexes: () => {
            return internalRef.current?.getSelectedIndexes() ?? [];
          },
          setActiveIndex: (index: number) => {
            internalRef.current?.setActiveIndex(index);
          },
          getActiveIndex: () => {
            return internalRef.current?.getActiveIndex() ?? -1;
          },
          scrollToPosition: (position: number) => {
            internalRef.current?.scrollToPosition(position);
          },
          setActiveFilter: (filter?: (item: ContactListItem) => boolean) => {
            internalRef.current?.setActiveFilter(filter);
          },
          setSortingProps: (props: SortingProps<ContactListItem>) => {
            internalRef.current?.setSortingProps(props);
          },
          getSortingProps: () => {
            return (
              internalRef.current?.getSortingProps() ??
              ({} as SortingProps<ContactListItem>)
            );
          },
        }),
        []
      );
      //#endregion

      //#region ------------------------------ Components
      const noItemsScreen = (
        <div className={classNames(classes.noItemsScreen, className)}>
          <div className={classes.noItemsIconContainer}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className={classes.noItemsText}>
                  {t('contacts:contactList.noItems')}
                </span>
              }
            />
          </div>
        </div>
      );
      //#endregion

      if (items.length === 0 && propSearchItemCount > 0 && !searchAnyway) {
        return (
          <div className={classNames(classes.noItemsScreen, className)}>
            <div className={classes.searchAnywayContainer}>
              <div className={classes.searchAnywayText}>
                {t('components:filter.thresholdScreen.content1')}
              </div>
              <div
                className={classes.tryAnyway}
                onClick={() => setSearchAnyway(true)}
              >
                {t('components:filter.thresholdScreen.tryAnyway')}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id="prio-contact-list"
          className={classNames(classes.root, className)}
        >
          {onlineLoading && (
            <div className={classes.loadingOverlay}>
              <PrioSpinner />
            </div>
          )}
          <div style={{ height: '100%' }}>
            <VirtualList.ArrowKeyList<ContactListItem>
              id="contact-list"
              items={items}
              noItemsScreen={noItemsScreen}
              enableActionBar={false}
              rowHeight={listItemHeight}
              rowsAreSelectable={selectable}
              enableHeader={false}
              ref={internalRef}
              onSelectionChange={handleSelect}
              onRow={handleOnRow}
              onActiveChange={handleOnActiveChange}
              onCheckEquality={(a, b) => {
                if (a.type === 'contact' && b.type === 'contact') {
                  return (
                    (a.value as Contact).contactId ===
                    (b.value as Contact).contactId
                  );
                }
                if (a.type === 'company' && b.type === 'company') {
                  return a.value.companyId === b.value.companyId;
                }
                return false;
              }}
              rowRenderer={({ index, item, isScrolling, isVisible }) => {
                let title: string = '';
                let description: string = '';
                let isArchived = false;
                if (item.type === 'contact') {
                  const contact = item.value as ExternalContact &
                    InternalContact;

                  title = `${contact.firstName} ${contact.lastName}`;
                  description =
                    getCompanyById(contact.companyId)?.fullName ?? '';
                  isArchived = contact.isArchived;
                } else {
                  const company = item.value as Company;
                  title = company.fullName;
                  description = company.shortName;
                  isArchived = company.isArchived;
                }
                return (
                  <div
                    key={index}
                    className={classNames(
                      'contact-list-item-content',
                      classes.listItemContent,
                      { [classes.archivedContact]: isArchived },
                      listItemClassName
                    )}
                  >
                    <div
                      className={classes.listItemMainContent}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        paddingLeft: !selectable ? 32 : 16,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                        }}
                      >
                        <span>{title}</span>
                        {isArchived && (
                          <span>{` (${t('contacts:contactDetails.archived')})`}</span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color:
                            'var(--prioCl-color-application-typography-muted)',
                        }}
                      >
                        {description}
                      </div>
                    </div>
                    {!isScrolling && isVisible && (
                      <div
                        className={classNames(
                          'contact-list-item-actions',
                          classes.listItemActions
                        )}
                      >
                        <SendEmailButton type={item.type} value={item.value} />
                        {item.type === 'contact' && (
                          <CallButton
                            phoneLinePreference={phoneLinePreference}
                            getContactById={getContactById}
                            contact={item.value as Contact}
                          />
                        )}
                        <CopyContactToClipboard
                          type={item.type}
                          value={item.value}
                          getCompanyById={getCompanyById}
                        />
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      );
    }
  )
);

export default ContactList;

interface CallButtonProps {
  phoneLinePreference: PhoneLinePreference;
  getContactById: (contactId: ContactId) => Contact | undefined;
  contact: Contact;
}

const CallButton: React.FC<CallButtonProps> = (props) => {
  //#region ------------------------------ Defaults
  const { phoneLinePreference, getContactById, contact } = props;
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Tooltip title={t('contacts:contactDetails.actions.call')}>
      <Button
        iconProp={['fal', 'phone']}
        type={'default'}
        style={{
          backgroundColor: 'transparent',
        }}
        iconColor="var(--prioCl-color-base-primary-default)"
        href={getCallingHref(
          phoneLinePreference,
          getContactById,
          contact.contactId
        )}
      />
    </Tooltip>
  );
};

interface SendEmailButtonProps {
  value: Contact | Company;
  type: 'contact' | 'company';
}

const SendEmailButton: React.FC<SendEmailButtonProps> = (props) => {
  //#region ------------------------------ Defaults
  const { value, type } = props;
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isCreatingMail, setIsCreatingMail] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOpenEMailMeFromContacts = async () => {
    setIsCreatingMail(true);
    const { data: message } = await apiCreateMessageDraft('me');
    if (message) {
      if (type === 'contact') {
        const contact = value as ExternalContact & InternalContact;
        message.toRecipients = [
          {
            emailAddress: {
              name: '',
              address: contact.eMail ?? contact.eMail2,
            },
          },
        ];
      } else {
        const company = value as Company;
        message.toRecipients = [
          {
            emailAddress: {
              name: '',
              address: company.offices && company.offices[0].eMail,
            },
          },
        ];
      }
      await apiUpdateMessageDraft('me', message.id, message);
      const width = window.screen.availWidth / 2;
      const height = window.screen.availHeight * 0.6;
      window.open(
        `/view/me/composer/${message.id}`,
        '_blank',
        `width=${width},height=${height},top=${height / 4}`
      );
    } else {
      notification.open({
        message: t('common:error'),
        description: t('mail:errorMessages.messages.createDraftError'),
      });
    }
    setIsCreatingMail(false);
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Tooltip title={t('contacts:contactDetails.actions.email')}>
      {isCreatingMail ? (
        <PrioSpinner size="small" />
      ) : (
        <Button
          iconProp={['fal', 'envelope']}
          type="default"
          style={{
            backgroundColor: 'transparent',
          }}
          iconColor="var(--prioCl-color-base-primary-default)"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleOpenEMailMeFromContacts();
          }}
        />
      )}
    </Tooltip>
  );
};

interface CopyContactToClipboardProps {
  type: 'contact' | 'company';
  value: Contact | Company;
  getCompanyById: (companyId: string) => Company | undefined;
}

const CopyContactToClipboard: React.FC<CopyContactToClipboardProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { type, value, getCompanyById } = props;
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [clicked, setClicked] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const copyCodeToClipboard = () => {
    setClicked(true);
    if (type === 'contact') {
      const contact = value as ExternalContact & InternalContact;
      const company = getCompanyById(contact.companyId);
      const office = company?.offices.find(
        (_office) => _office.officeId === contact.officeId
      );
      copyToClipboard(
        `${contact.firstName} ${contact.lastName} ${
          contact.eMail
            ? `\n${t('contacts:contactDetails.labels.email')}: ${contact.eMail}`
            : ''
        }${
          contact.phone
            ? `\n${t('contacts:contactDetails.labels.phone')}: ${contact.phone}`
            : ''
        }${
          contact.phone2
            ? `\n${t('contacts:contactDetails.labels.phone2')}: ${
                contact.phone2
              }`
            : ''
        }${
          contact.cellphone
            ? `\n${t('contacts:contactDetails.labels.cellphone')}: ${
                contact.cellphone
              }`
            : ''
        }${office?.street ? `\n${office?.street}` : ''}${
          office?.zip ? `\n${office?.zip} ` : ''
        }${office?.city ? `${office?.city}` : ''}`
      );
    } else {
      const company = value as Company;
      copyToClipboard(
        `${company.fullName}  ${
          company.offices.find((x) => x.isMainOffice)?.eMail
            ? `\n${t('contacts:companyPreview.labels.eMail')} ${
                company.offices[0]?.eMail
              }`
            : ''
        }${
          company.offices[0]?.phone
            ? `\n${t('contacts:companyPreview.labels.phone')} ${
                company.offices[0]?.phone
              }`
            : ''
        }${
          company.offices[0]?.street ? `\n${company.offices[0]?.street}` : ''
        }${company.offices[0]?.zip ? `\n${company.offices[0]?.zip} ` : ''}${
          company.offices[0]?.city ? `${company.offices[0]?.city}` : ''
        }`
      );
      setTimeout(() => setClicked(false), 750);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Tooltip title={t('contacts:contactDetails.actions.copy')}>
      <Button
        iconProp={['fal', clicked ? 'check' : 'copy']}
        type="default"
        style={{
          backgroundColor: 'transparent',
        }}
        iconColor="var(--prioCl-color-base-primary-default)"
        onClick={(e: React.MouseEvent) => {
          copyCodeToClipboard();
          e.stopPropagation();
          e.preventDefault();
        }}
      />
    </Tooltip>
  );
};
